import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, shareReplay, tap, throwError, type Observable } from 'rxjs';

import { renderMarkdown, type MarkdownHeading } from './markdown';

/**
 * Les documents lisibles depuis l'interface (odoo-5133). Ils vivent sous `docs/` dans le
 * dépôt, à côté du code qu'ils décrivent, et sont recopiés tels quels dans le site par la
 * configuration d'`angular.json` : une seule source, relue en revue de MR comme le reste,
 * et personne n'a à maintenir une deuxième copie dans un CMS.
 */
export type CleDocument = 'guide' | 'nouveautes';

const FICHIERS: Record<CleDocument, string> = {
	guide: 'docs/guide-back-office.md',
	nouveautes: 'docs/notes-de-version.md',
};

/** Ce que la page reçoit : le HTML rendu et les titres, pour le sommaire. */
export interface DocumentRendu {
	html: string;
	titres: MarkdownHeading[];
}

/**
 * Clé de stockage local : la dernière livraison que cet utilisateur a lue. Le stockage est
 * par navigateur, ce qui est exactement la portée voulue — « ai-je lu ces nouveautés »
 * regarde la personne devant l'écran, pas le compte, et ne mérite ni table ni appel réseau.
 */
const CLE_VUES = 'coverUpdate.nouveautes.vues';
const CLE_SESSION_ANNONCEE = 'coverUpdate.nouveautes.annonceeSession';

/**
 * Rendu quand le stockage du navigateur est inaccessible — navigation privée, réglage
 * d'entreprise. Sans mémoire, la pastille reparaîtrait à chaque chargement et deviendrait
 * un bruit qu'on cesse d'ouvrir : on la tient éteinte plutôt que d'insister.
 */
const SANS_MEMOIRE = 'sans-memoire (stockage indisponible)';

/**
 * Repère la dernière livraison décrite par les notes de version : le premier titre de
 * niveau 2 qui porte une année. La section « Sur dev » n'en porte pas, elle est donc
 * ignorée — ce qui est voulu : on ne prévient personne pour ce que le parc n'a pas reçu.
 */
export function derniereLivraisonDe(titres: MarkdownHeading[]): string | null {
	return titres.find((t) => t.level === 2 && /\b(19|20)\d{2}\b/.test(t.text))?.text ?? null;
}

@Injectable({ providedIn: 'root' })
export class DocsService {
	private readonly http = inject(HttpClient);
	private readonly cache = new Map<CleDocument, Observable<DocumentRendu>>();

	/** Titre de la dernière livraison connue, une fois les notes chargées. */
	private readonly derniereLivraison = signal<string | null>(null);
	private readonly derniereLue = signal<string | null>(lire(CLE_VUES));

	/**
	 * Vrai quand les notes annoncent une livraison que cet utilisateur n'a pas encore
	 * ouverte. Sur une première visite, rien n'est enregistré : la pastille s'affiche, et
	 * c'est bien ce qu'on veut — elle sert aussi à faire découvrir la page.
	 */
	readonly nouveautesNonLues = computed(() => {
		const derniere = this.derniereLivraison();
		const lue = this.derniereLue();
		if (lue === SANS_MEMOIRE) return false;
		return derniere !== null && derniere !== lue;
	});

	/** Le titre à annoncer, pour le message qui invite à aller le lire. */
	readonly titreNouveaute = computed(() => this.derniereLivraison());

	charger(cle: CleDocument): Observable<DocumentRendu> {
		const dejaLa = this.cache.get(cle);
		if (dejaLa) return dejaLa;

		const flux = this.http.get(FICHIERS[cle], { responseType: 'text' }).pipe(
			map((source) => {
				const { html, headings } = renderMarkdown(source);
				return { html, titres: headings };
			}),
			tap(({ titres }) => {
				if (cle === 'nouveautes') this.derniereLivraison.set(derniereLivraisonDe(titres));
			}),
			catchError((erreur) => {
				// Un document absent ne doit pas laisser la page tourner dans le vide : le cache
				// est vidé pour qu'un « réessayer » reparte du réseau plutôt que de l'erreur.
				this.cache.delete(cle);
				return throwError(() => erreur);
			}),
			shareReplay({ bufferSize: 1, refCount: false }),
		);

		this.cache.set(cle, flux);
		return flux;
	}

	/**
	 * Charge les notes en tâche de fond, uniquement pour savoir s'il y a du nouveau. Le
	 * résultat est le même flux que celui de la page : ouvrir « Nouveautés » ensuite ne
	 * relance aucun téléchargement.
	 */
	surveillerNouveautes(): void {
		this.charger('nouveautes').subscribe({ error: () => undefined });
	}

	/** L'utilisateur a ouvert les nouveautés : la pastille s'éteint, ici et au prochain démarrage. */
	marquerNouveautesLues(): void {
		const derniere = this.derniereLivraison();
		if (!derniere) return;
		this.derniereLue.set(derniere);
		ecrire(CLE_VUES, derniere);
	}

	/**
	 * Vrai une seule fois par session de navigation : le message d'invitation se montre au
	 * premier écran, pas à chaque changement de page.
	 */
	doitAnnoncerDansCetteSession(): boolean {
		if (!this.nouveautesNonLues()) return false;
		const derniere = this.derniereLivraison();
		if (lireSession(CLE_SESSION_ANNONCEE) === derniere) return false;
		ecrireSession(CLE_SESSION_ANNONCEE, derniere ?? '');
		return true;
	}
}

// Le stockage du navigateur peut être refusé (navigation privée, réglage d'entreprise). On
// n'en fait pas une panne : sans mémoire, la pastille se comporte comme si tout était lu,
// plutôt que de réapparaître à chaque chargement et de devenir un bruit qu'on n'ouvre plus.
function lire(cle: string): string | null {
	try {
		return localStorage.getItem(cle);
	} catch {
		return SANS_MEMOIRE;
	}
}

function ecrire(cle: string, valeur: string): void {
	try {
		localStorage.setItem(cle, valeur);
	} catch {
		/* rien à faire : la pastille restera éteinte pour cette session */
	}
}

function lireSession(cle: string): string | null {
	try {
		return sessionStorage.getItem(cle);
	} catch {
		return SANS_MEMOIRE;
	}
}

function ecrireSession(cle: string, valeur: string): void {
	try {
		sessionStorage.setItem(cle, valeur);
	} catch {
		/* idem */
	}
}
